import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchReset } from '../../actions/fetch.actions';
import AdminContactCityFields from '../../components/admin/contacts/AdminContactCityFields';
import AdminAddContactOrganizationsFields from '../../components/admin/contacts/AdminContactOrganizationsFields';
import AdminContactSharedFields from '../../components/admin/contacts/AdminContactSharedFields';
import { INPUT_STATES } from '../../components/forms/forms.utils';
import TextArea from '../../components/forms/TextArea';
import { ADMIN_CONTACT_SCHOOL } from '../../constants/admin.contacts.constants';
import { MODALS } from '../../constants/modals.constants';
import formValidators from '../../form-validators';
import { useForm } from '../../hooks/useForm.hook';
import { useModal } from '../../hooks/useModal.hook';
import { getCanSubmitContact } from '../../lib/contact.utils';
import { ADMIN_CONTACT_PARTNER_ORGANIZATIONS_RESOURCE } from '../../resources/admin/admin.contact.partner.organizations.resource';
import { ADMIN_CONTACT_SCHOOL_ORGANIZATIONS_RESOURCE } from '../../resources/admin/admin.contact.school.organizations.resource';
import { ADMIN_CONTACTS_RESOURCE } from '../../resources/admin/admin.contacts.resource';
import { currentUserSelector } from '../../selectors/auth.selectors';
import contactsService from '../../services/api/contacts.service';
import contactsPermissionsService from '../../services/permissions/contacts.permissions.service';
import { ERROR_TEXTS } from '../../services/response.validators';
import Modal from '../Modal';
import ModalTitle from '../ModalTitle';
import ContactEmailExistsWarningModal from './ContactEmailExistsWarningModal';
import ContactWithNoPermissionWarningModal from './ContactWithNoPermissionWarningModal';

const INPUT_NAME = 'spo-add-contact-textfield';

const AddContactModal = ({ isOpen, onClose, onCreated }) => {
  const dispatch = useDispatch();
  const modalContentRef = useRef(null);
  const [validation, setValidation] = useState({});
  const currentUser = useSelector(currentUserSelector);
  const canAddPartnerContacts = contactsPermissionsService.canAddPartnerContacts(currentUser);
  const { data, isSubmitting, input, submitStart, submitOk, submitError, reset, error } = useForm({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    type: !canAddPartnerContacts ? ADMIN_CONTACT_SCHOOL : '',
    organization: '',
    nets: [],
    managements: [],
    communities: [],
    schools: [],
    branches: [],
    roles: [],
    function: '',
    role: '',
    remark: ''
  });
  const contactEmailExistsWarningModal = useModal(MODALS.CONTACT_EMAIL_EXISTS_WARNING_MODAL);
  const contactWithNoPermissionWarningModal = useModal(MODALS.CONTACT_WITH_NO_PERMISSION_WARNING_MODAL);

  const validators = {
    email: formValidators.email,
    phone: formValidators.phoneSimple
  };

  const handleConfirm = () => {
    submitStart();
    contactsService.add(
      { ...data },
      {
        onSuccess: (createdContact) => {
          submitOk();
          dispatch(fetchReset(ADMIN_CONTACTS_RESOURCE));
          handleClose();
          reset();
          if (onCreated) {
            onCreated(createdContact);
          }
        },
        onError: (message) => {
          submitError();
          if (message === ERROR_TEXTS.CONTACT_WITH_NO_PERMISSION) {
            contactWithNoPermissionWarningModal.onOpen();
          }
          if (message === ERROR_TEXTS.CONTACT_WITH_EMAIL_EXISTS) {
            contactEmailExistsWarningModal.onOpen();
          }
        }
      }
    );
  };

  const handleClose = () => {
    Object.keys(data).forEach((key) => {
      input('', `${key}`);
    });

    dispatch(fetchReset(ADMIN_CONTACT_PARTNER_ORGANIZATIONS_RESOURCE));
    dispatch(fetchReset(ADMIN_CONTACT_SCHOOL_ORGANIZATIONS_RESOURCE));
    reset();
    onClose();
  };

  const handleEnter = (event) => {
    if (event.code === 'Enter' && getCanSubmitContact(data, validation)) {
      handleConfirm();
    }
  };

  const handleInput = (value, field) => {
    if (validators[field]) {
      setValidation({ ...validation, [field]: validators[field](value) });
    }
    input(value, field);
  };

  const handleRoleAdd = (roleData) => {
    input([...data?.roles, { ...roleData, id: (data?.roles || []).length }], 'roles');
  };

  const handleRoleUpdate = (roleData) => {
    handleInput(
      data?.roles?.map((r, index) => {
        if (index === roleData?.id) {
          return roleData;
        }

        return r;
      }),
      'roles'
    );
  };

  const handleRoleDelete = (id) => {
    handleInput(
      data?.roles?.filter((r, index) => index !== id),
      'roles'
    );
  };

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        document.getElementsByName(INPUT_NAME)?.[0]?.focus();
      }, 10);
    }
  }, [isOpen]);

  const sharedSubfieldsProps = {
    data,
    canUpdate: true,
    validation,
    isSubmitting,
    onInput: handleInput,
    parentElement: modalContentRef.current
  };

  return (
    <Modal
      ref={modalContentRef}
      className="spo-o-add-contact-modal"
      title={<ModalTitle hasError={!!error} title="Contact toevoegen" />}
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
      canConfirm={getCanSubmitContact(data, validation)}
      isSubmitting={isSubmitting}
      onConfirmLabel="Toevoegen"
      onConfirmIcon="ai-check-1"
      width={1200}
    >
      <AdminContactSharedFields inputName={INPUT_NAME} onEnter={handleEnter} {...sharedSubfieldsProps} />
      <AdminAddContactOrganizationsFields
        type={data?.type}
        roles={data?.roles}
        onAdd={handleRoleAdd}
        onUpdate={handleRoleUpdate}
        onDelete={handleRoleDelete}
        {...sharedSubfieldsProps}
      />
      <AdminContactCityFields
        inputName={INPUT_NAME}
        onEnter={handleEnter}
        parentElement={modalContentRef.current}
        {...sharedSubfieldsProps}
      />
      <TextArea
        label="Opmerkingen"
        name={INPUT_NAME}
        value={data?.remark}
        state={INPUT_STATES.DEFAULT}
        onKeyDown={handleEnter}
        className="u-margin-top"
        onChange={(value) => handleInput(value, 'remark')}
        disabled={isSubmitting}
      />
      {!!error && <p className="u-text-danger">{error}</p>}
      <ContactEmailExistsWarningModal {...contactEmailExistsWarningModal} />
      <ContactWithNoPermissionWarningModal {...contactWithNoPermissionWarningModal} />
    </Modal>
  );
};

export default AddContactModal;
